import React, { useMemo } from 'react';
import { createAvatar } from '@dicebear/core';
import { identicon, initials } from '@dicebear/collection';
import { getUserFullName, classNames } from '../helpers/functions';

import { AvatarUser, AvatarText, AvatarSrc } from './tailwindAvatar';

const Avatar = ({
	size = 'xs',
	src,
	children,
	title,
	description,
	onClick,
	user,
}) => {
	const isClickable = typeof onClick === 'function';

	const titleLabel = user ? getUserFullName(user) : title;

	return (
		<div
			className={classNames(
				'flex space-x-3',
				isClickable ? 'cursor-pointer' : ''
			)}
			onClick={() => (isClickable ? onClick() : null)}
		>
			<div className="flex-shrink-0">
				{!src && !children && <AvatarUser size={size} />}
				{src && <AvatarSrc size={size} src={src} />}
				{children && <AvatarText size={size}>{children}</AvatarText>}
			</div>
			{titleLabel || description ? (
				<div className="min-w-0 flex-1">
					{titleLabel && (
						<p className="text-xs font-medium text-gray-500">{titleLabel}</p>
					)}
					{description && (
						<p className="text-xs text-gray-400">{description}</p>
					)}
				</div>
			) : null}
		</div>
	);
};

const COLLECTION_MAPPING = (key, size) =>
	({
		identicon: {
			radius: 20,
			backgroundColor: ['b6e3f4', 'c0aede', 'd1d4f9', 'ffd5dc', 'ffdfbf'],
		},
		thumbs: {
			eyes: [
				'variant4W10',
				'variant5W10',
				'variant6W10',
				'variant8W10',
				'variant8W14',
			],
			mouth: ['variant1', 'variant2'],
			radius: 50,
			backgroundColor: ['b6e3f4', 'c0aede', 'd1d4f9', 'ffd5dc', 'ffdfbf'],
		},
		shapes: {
			radius: 50,
			backgroundColor: ['b6e3f4', 'c0aede', 'd1d4f9', 'ffd5dc', 'ffdfbf'],
		},
		initials: {
			radius: 50,
			scale: size === 'xl' ? 50 : 80,
			backgroundColor: [
				'00acc1',
				'1e88e5',
				'5e35b1',
				'7cb342',
				'8e24aa',
				'039be5',
				'43a047',
				'00897b',
				'3949ab',
				'c0ca33',
			],
		},
	}[key]);

export const AvatarGenerated = ({
	id,
	size = 'md',
	type = 'person',
	className,
	config,
}) => {
	const avaSize = {
		xxxs: 16,
		xxs: 20,
		xs: 32,
		sm: 48,
		md: 64,
		lg: 80,
		xl: 96,
	}[size];

	const collection = type === 'person' ? initials : identicon;
	const collectionConfig = COLLECTION_MAPPING(
		type === 'person' ? 'initials' : 'identicon',
		size
	);

	const avatar = useMemo(() => {
		return id
			? createAvatar(collection, {
					seed: id,
					size: avaSize,
					...collectionConfig,
					...config,
			  }).toDataUriSync()
			: null;
	}, [id]);
	return <img src={avatar} alt="Avatar" className={classNames(className)} />;
};

export default Avatar;
